/**
 * RouterTransition — wraps app `<Routes>` with `AnimatePresence`
 * keyed by the location path so each route entry / exit animates.
 *
 *  - PUSH:    new page slides in from the right (+12px) with fade.
 *  - POP:     new page slides in from the left  (-12px) with fade.
 *  - REPLACE: straight cross-fade, no slide.
 *
 * Tokens come from `@/shared/motion/tokens` so route motion shares the
 * same easing + duration as Sheet / Modal / MorphPill.
 *
 * Usage — pass the `<Routes>` element via the `routes` prop:
 *
 *   <RouterTransition routes={(loc) => (
 *     <Routes location={loc}>
 *       <Route path="/" element={<Home />} />
 *       ...
 *     </Routes>
 *   )} />
 *
 * Note: the location is forwarded to `<Routes>` so AnimatePresence sees
 * the OLD route during exit. This is the standard react-router + framer
 * pattern for page transitions.
 */
import React from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { DURATION, EASE } from "@/shared/motion/tokens";

export default function RouterTransition({ routes, transitionKey }) {
  const location = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"

  const dx = navType === "POP" ? -10 : navType === "REPLACE" ? 0 : 10;
  // Optional key collapsing — lets the host opt out of route animation
  // between paths that share an underlying surface (e.g. "/" and
  // "/account" both render the booking map; only the overlay differs).
  const key = typeof transitionKey === "function" ? transitionKey(location) : location.pathname;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={key}
        initial={{ opacity: 0, x: dx }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -dx * 0.5 }}
        transition={{ duration: DURATION.normal, ease: EASE.out }}
        data-router-transition
      >
        {routes(location)}
      </motion.div>
    </AnimatePresence>
  );
}
